// roleSchema.js — shared definitions + validation for CUSTOM ROLES
// (admin_config key `roleDefinitions`).
//
// An entry = { key, value, label, description?, enabled?, builtIn?,
//              capabilities?, scope? }.
//   key          — immutable slug, the identity field (mergeByKey keys on it)
//   value        — the Entra appRole string (resolution keys on it; unique)
//   builtIn      — true for the 8 shipped roles; they omit capabilities/scope
//                  so the code defaults in auth.js/scope.js apply untouched
//   capabilities — custom roles only: booleans overlaid onto an all-false base
//   scope        — custom roles only: { type, value } compiled onto the two
//                  SQL primitives in scope.js (clinic IN-list / region IN-list)
//
// Security: `impersonate` IS grantable to a custom role (decision 2026-07-04,
// for the Directors of Operations) — safe because impersonation targets are
// capped at lesser roles in auth.js (SuperAdmin never assumable, Supervisor
// only by a real SuperAdmin), every non-GET is rejected while assuming, and
// view caps are intersected with the real caller's so an impersonator can
// never read more than their own role allows. The implication rules below stop
// a SuperAdmin authoring impossible combinations (e.g. delete without edit).
// Fail closed: anything this validator rejects never reaches the server-side
// role cache (roleConfig.js).
//
// HARD RULE (same as configSchema.js): runtime-agnostic — pure data + pure
// functions only, so both the API and the SPA can consume it untouched.

/** The 8 shipped roles — custom role values must never collide with these. */
export const BUILT_IN_ROLE_NAMES = Object.freeze([
  'SuperAdmin',
  'Supervisor',
  'SupervisorView',
  'Admin',
  'HoRO',
  'ClinicManager',
  'ClinicMailbox',
  'ReadOnly',
]);

/**
 * Role priority — lower number = higher privilege. Impersonation may target only
 * roles with a strictly greater priority (lesser privilege). Gaps (e.g. HoRO 20,
 * CM 30) leave room for custom roles in between.
 */
export const BUILT_IN_ROLE_PRIORITIES = Object.freeze({
  SuperAdmin:     0,
  Supervisor:     1,
  Admin:          2,
  SupervisorView: 3,
  HoRO:          20,
  ClinicManager: 30,
  // Below ClinicManager — a shared clinic login, the least-privileged authority.
  ClinicMailbox: 40,
  ReadOnly:      90,
});

const DEFAULT_CUSTOM_PRIORITY = 50;
const MIN_PRIORITY = 0;
const MAX_PRIORITY = 999;

/** Per-role session auto-lock bounds (ms). A blank/absent timing inherits the
 *  code default (pinLockLogic.js INACTIVITY_MS / HIDDEN_GRACE_MS); `exempt` means
 *  manual-lock only (generalises the SuperAdmin exemption to any role). Bounds
 *  keep an admin from setting 0 (instant lock) or an effectively-never value. */
export const SESSION_LOCK_INACTIVITY_MIN_MS   = 60_000;      // 1 minute
export const SESSION_LOCK_INACTIVITY_MAX_MS   = 7_200_000;   // 2 hours
export const SESSION_LOCK_HIDDEN_GRACE_MIN_MS = 5_000;       // 5 seconds
export const SESSION_LOCK_HIDDEN_GRACE_MAX_MS = 600_000;     // 10 minutes

/** Numeric priority for a role definition or built-in value string. */
export function resolveRolePriority(entry) {
  if (entry != null && typeof entry === 'object' && Number.isInteger(entry.priority)) {
    return entry.priority;
  }
  const value = typeof entry === 'string' ? entry : entry?.value;
  if (value && Object.prototype.hasOwnProperty.call(BUILT_IN_ROLE_PRIORITIES, value)) {
    return BUILT_IN_ROLE_PRIORITIES[value];
  }
  return DEFAULT_CUSTOM_PRIORITY;
}

/** True when `assumedRole` is strictly lower privilege than `callerPriority`. */
export function canAssumeRole(callerPriority, assumedRole, assumedDef = null) {
  if (assumedRole === 'SuperAdmin') return false;
  const assumedPriority = resolveRolePriority(assumedDef || { value: assumedRole });
  return assumedPriority > callerPriority;
}

/** Booleans a custom role may set. */
export const CUSTOM_CAPABILITY_KEYS = Object.freeze([
  'submitCase', 'editCase', 'changeStatusAndCode',
  'write', 'comment', 'resolve', 'reopen', 'del',
  // manageReq = "can add requirements"; respondReq = "can respond to requirements"
  // — two independent capabilities (a team may respond without adding, or comment
  // + respond without editing). See requirement_permissions_logic.md.
  // dashboardView / knowledgeView / caseReportView replaced the single
  // `reportView` (2026-07): Dashboard page, Knowledge guide, and the case-file
  // Report tab + file downloads are now three independent surfaces.
  'costView', 'costEdit', 'dashboardView', 'knowledgeView', 'caseReportView',
  'manageReq', 'respondReq', 'assignTags', 'createTags',
  // waiveReq = "can waive a required document" — record (with a reason) that a
  // matrix-required document does not exist, or withdraw such a record, on an
  // existing case. Independent of manageReq/respondReq. Built-in default: on
  // for SuperAdmin + Supervisor only (required_evidence_logic.md §3).
  'waiveReq',
  'privateView', 'impersonate',
  // Clinic mailbox sub-identity onboarding (ClinicAuthWorkflow):
  //  - clinicAccessRequest: a shared clinic mailbox may register a named person
  //    and request access (held by the ClinicMailbox role).
  //  - manageClinicAccess: approve/deny those requests from the Notifications
  //    inbox (Supervisor tier and up).
  //  - shareCaseAccess: grant a registered clinic staff member temporary access
  //    to a single case (HoRO + Supervisor tier for now — see the access-model
  //    note in clinic_access_model.md; may extend to Clinic Managers later).
  'clinicAccessRequest', 'manageClinicAccess', 'shareCaseAccess',
  // manageSla = "can manage the Code 4/5 response SLA" — lift an acknowledgement
  // hold, suppress the timers on a case (with a reason) and resume them
  // (POST /api/cases/{id}/sla/{lift|suppress|resume}). Built-in default: on for
  // SuperAdmin + Supervisor only (sla_escalation_logic.md §4).
  'manageSla',
]);

/** Retired keys still present in stored rows — accepted on input, never
 *  re-emitted. `reportView` gated the Dashboard until the 2026-07 split;
 *  normalizeCapabilities migrates a stored `reportView: true` to
 *  `dashboardView` when no explicit dashboardView is set (upgrade only — a
 *  stored false is ignored, the base default wins). */
export const LEGACY_CAPABILITY_KEYS = Object.freeze(['reportView']);

/** capability X true requires capability Y true. */
export const CAPABILITY_IMPLICATIONS = Object.freeze([
  { when: 'costEdit',           needs: 'costView' },
  { when: 'createTags',         needs: 'assignTags' },
  { when: 'del',                needs: 'editCase' },
  // NOTE: 'manageReq' no longer requires 'comment' — adding requirements is now an
  // independent capability, composable with comment/respond as teams need.
]);

/** Fail-closed base — custom capabilities overlay onto this. */
export const EMPTY_CAPABILITIES = Object.freeze(
  Object.fromEntries(CUSTOM_CAPABILITY_KEYS.map(k => [k, false])),
);

/** Default base for CUSTOM roles: fail-closed, except the Knowledge guide —
 *  help content is deliberately opt-out, not opt-in (decision 2026-07-21). */
export const CUSTOM_ROLE_BASE_CAPABILITIES = Object.freeze({
  ...EMPTY_CAPABILITIES,
  knowledgeView: true,
});

/**
 * Normalise stored capabilities against a base: an explicit stored boolean
 * wins per key; a MISSING key keeps the base value. Pass a built-in role's
 * matrix row as `base` so capability keys added after the row was saved
 * default to the code matrix instead of silently off (the reportView lesson);
 * custom roles pass CUSTOM_ROLE_BASE_CAPABILITIES (fail closed).
 * Also migrates legacy `write` / `reportView` and enforces implications.
 * @returns {Record<string, boolean>}
 */
export function normalizeCapabilities(caps, base = CUSTOM_ROLE_BASE_CAPABILITIES) {
  const stored = caps && typeof caps === 'object' ? caps : {};
  const c = {};
  for (const k of CUSTOM_CAPABILITY_KEYS) {
    c[k] = typeof stored[k] === 'boolean' ? stored[k] : base[k] === true;
  }

  // Legacy `reportView` (pre-split it meant the Dashboard) → dashboardView.
  // Upgrade only: rows saved between v0.29.13 and the split may carry a
  // baked-in false that never reflected intent — the base default wins there.
  if (typeof stored.dashboardView !== 'boolean' && stored.reportView === true) {
    c.dashboardView = true;
  }

  // Legacy `write` → all three case actions — only for a genuine pre-v0.29.13
  // row (write stored explicitly, none of the three action keys present). Keyed
  // off the STORED row, not the merged result: a base default must never
  // resurrect an action the row explicitly revoked.
  if (stored.write === true
      && typeof stored.submitCase !== 'boolean'
      && typeof stored.editCase !== 'boolean'
      && typeof stored.changeStatusAndCode !== 'boolean') {
    c.submitCase = true;
    c.editCase = true;
    c.changeStatusAndCode = true;
  }
  c.write = c.submitCase || c.editCase;

  for (const { when, needs } of CAPABILITY_IMPLICATIONS) {
    if (c[when] && !c[needs]) c[when] = false;
  }
  return c;
}

export const SCOPE_TYPES = Object.freeze([
  'all', 'countryGroup', 'regions', 'clinics',
  'assignedRegion', 'assignedClinics',
]);

/**
 * Country groups → the TherapieMaps clinic `country` value they compile from.
 * TM already groups the island of Ireland: the 4 NI clinics carry
 * country='Ireland' (region 'IE - North'), so IOI = TM 'Ireland' and
 * GB = TM 'United Kingdom' (England/Scotland/Wales only). NEVER compile these
 * from cases.country — the SPA's normaliseCountry folds NI into 'United Kingdom'.
 * US is the clean case: TM country='United States' and cases.country='United States'
 * align (normaliseCountry passes it through), no NI-style fold to worry about.
 */
export const COUNTRY_GROUPS = Object.freeze(['IOI', 'GB', 'US']);
export const COUNTRY_GROUP_TM_COUNTRY = Object.freeze({
  IOI: 'Ireland',
  GB:  'United Kingdom',
  US:  'United States',
});

// Region-name prefix → country group. Region names are the Maps API's only
// geography signal ('IE - North', 'GB - London City', 'US - East') — its clinic
// payload carries no country field, so the TM country has to be derived from
// this prefix. 'IE - North' (the NI clinics) lands in Ireland/IOI, the same
// island-of-Ireland grouping the PA blobs' country column encodes.
const REGION_PREFIX_GROUP = Object.freeze({ IE: 'IOI', GB: 'GB', US: 'US' });

/**
 * TM `country` value for a region name, from its 'XX - ' prefix.
 * @param {string|null|undefined} region e.g. 'IE - North'
 * @returns {string|null} 'Ireland' | 'United Kingdom' | 'United States' | null
 */
export function countryForRegion(region) {
  const m = /^([A-Z]{2})\s*-\s/.exec(String(region || '').trim());
  const group = m ? REGION_PREFIX_GROUP[m[1]] : null;
  return group ? COUNTRY_GROUP_TM_COUNTRY[group] : null;
}

/**
 * Compile a country group to its region set from TherapieMaps clinic rows.
 * Region-set (not clinic-set) on purpose: new/renamed clinics inherit via the
 * region stamped on the case row, and closed clinics' historic cases stay
 * visible. Empty result → the caller's scope compiles to `AND 1=0` (fail closed
 * — covers the not-yet-trading clinics with blank geography).
 *
 * @param {Array<{country?: string, region?: string}>} clinics TM clinic rows
 * @param {string} group 'IOI' | 'GB' | 'US'
 * @returns {string[]} distinct region names, sorted
 */
export function regionsForCountryGroup(clinics, group) {
  const country = COUNTRY_GROUP_TM_COUNTRY[group];
  if (!country || !Array.isArray(clinics)) return [];
  const regions = new Set();
  for (const c of clinics) {
    if (String(c?.country || '').trim() !== country) continue;
    const region = String(c?.region || '').trim();
    if (region) regions.add(region);
  }
  return [...regions].sort();
}

// ── Validation ────────────────────────────────────────────────────────────────

const isBlank = (v) => v == null || String(v).trim() === '';
const norm = (v) => String(v ?? '').trim().toLowerCase();

const MAX_ROLES = 50;
const MAX_TEXT = 120;
const MAX_DESCRIPTION = 400;
const MAX_SCOPE_ITEMS = 200;
// Per-type item caps — must agree with the scope.js bind types so a valid
// entry can never silently truncate at bind time.
const MAX_SCOPE_ITEM_LEN = {
  clinics: 200,   // NVarChar(200)
  regions: 120,   // NVarChar(120), matches cases.region NVARCHAR(120)
};

// Accept retired keys on input so pre-split stored rows still validate; they
// are consumed by normalizeCapabilities and never re-emitted.
const CAPABILITY_KEY_SET = new Set([...CUSTOM_CAPABILITY_KEYS, ...LEGACY_CAPABILITY_KEYS]);
const SCOPE_TYPE_SET = new Set(SCOPE_TYPES);
const BUILT_IN_SET = new Set(BUILT_IN_ROLE_NAMES);
const BUILT_IN_NORM_SET = new Set(BUILT_IN_ROLE_NAMES.map(norm));

/** True when this entry describes one of the 8 shipped roles (code defaults
 *  apply). The seed predates the `builtIn` flag, so an entry whose value IS a
 *  built-in name counts too — the seeded 7 must publish as a verified no-op. */
export function isBuiltInRoleEntry(entry) {
  return entry?.builtIn === true || BUILT_IN_SET.has(entry?.value);
}

function validateCapabilities(caps, push) {
  if (caps == null || typeof caps !== 'object' || Array.isArray(caps)) {
    push('capabilities must be an object of booleans.');
    return;
  }
  for (const [k, v] of Object.entries(caps)) {
    if (!CAPABILITY_KEY_SET.has(k)) { push(`unknown capability "${k}".`); continue; }
    if (typeof v !== 'boolean') push(`capability "${k}" must be a boolean.`);
  }
  for (const { when, needs } of CAPABILITY_IMPLICATIONS) {
    if (caps[when] === true && caps[needs] !== true) {
      push(`${when} requires ${needs}.`);
    }
  }
}

function validateScopeList(list, what, push) {
  if (!Array.isArray(list) || list.length === 0) {
    push(`scope value must be a non-empty list of ${what}.`);
    return;
  }
  if (list.length > MAX_SCOPE_ITEMS) {
    push(`scope value has too many ${what} (max ${MAX_SCOPE_ITEMS}).`);
  }
  const maxLen = MAX_SCOPE_ITEM_LEN[what];
  const seen = new Set();
  for (const item of list) {
    if (isBlank(item) || typeof item !== 'string') { push(`scope ${what} entries cannot be empty.`); continue; }
    if (item.length > maxLen) push(`scope entry "${item.slice(0, 20)}…" is too long (max ${maxLen}).`);
    const k = norm(item);
    if (seen.has(k)) push(`duplicate scope entry "${item}".`);
    seen.add(k);
  }
}

function validatePriority(entry, push) {
  if (entry.value === 'SuperAdmin') return;
  if (entry.priority == null) {
    if (!isBuiltInRoleEntry(entry)) push('priority is required for custom roles.');
    return;
  }
  if (!Number.isInteger(entry.priority)) {
    push('priority must be a whole number.');
    return;
  }
  if (entry.priority < MIN_PRIORITY || entry.priority > MAX_PRIORITY) {
    push(`priority must be between ${MIN_PRIORITY} and ${MAX_PRIORITY}.`);
  }
}

function validateScope(scope, push) {
  if (scope == null || typeof scope !== 'object' || Array.isArray(scope)) {
    push('scope must be an object { type, value }.');
    return;
  }
  if (!SCOPE_TYPE_SET.has(scope.type)) {
    push(`scope type must be one of ${SCOPE_TYPES.join(', ')}.`);
    return;
  }
  if (scope.type === 'all') {
    if (scope.value != null) push('scope type "all" takes no value.');
    return;
  }
  if (scope.type === 'assignedRegion' || scope.type === 'assignedClinics') {
    if (scope.value != null) push(`scope type "${scope.type}" takes no value — resolved per person from TherapieMaps.`);
    return;
  }
  if (scope.type === 'countryGroup') {
    if (!COUNTRY_GROUPS.includes(scope.value)) {
      push(`country group must be one of ${COUNTRY_GROUPS.join(', ')}.`);
    }
    return;
  }
  validateScopeList(scope.value, scope.type, push);
}

/**
 * Validate the whole `roleDefinitions` array. AUTHORITATIVE shape used by the
 * server (via configSchema.js dispatch); the client calls the same function.
 * Self-contained on purpose — configWrite threads no context for this key.
 * @returns {{ ok: boolean, errors: string[], byId: Record<string,string[]> }}
 */
function validateBoundedMs(v, name, min, max, push) {
  if (v == null) return;                      // absent = inherit the code default
  if (!Number.isInteger(v)) { push(`${name} must be a whole number of milliseconds.`); return; }
  if (v < min || v > max) push(`${name} must be between ${min} and ${max} ms.`);
}

/** Validate the optional per-role session-lock override. Absent = inherit all
 *  defaults. Shape: { inactivityMs?, hiddenGraceMs?, exempt? }. */
function validateSessionLock(sessionLock, push) {
  if (sessionLock == null) return;
  if (typeof sessionLock !== 'object' || Array.isArray(sessionLock)) {
    push('sessionLock must be an object { inactivityMs?, hiddenGraceMs?, exempt? }.');
    return;
  }
  const allowed = new Set(['inactivityMs', 'hiddenGraceMs', 'exempt']);
  for (const k of Object.keys(sessionLock)) {
    if (!allowed.has(k)) push(`unknown sessionLock field "${k}".`);
  }
  validateBoundedMs(sessionLock.inactivityMs, 'sessionLock.inactivityMs',
    SESSION_LOCK_INACTIVITY_MIN_MS, SESSION_LOCK_INACTIVITY_MAX_MS, push);
  validateBoundedMs(sessionLock.hiddenGraceMs, 'sessionLock.hiddenGraceMs',
    SESSION_LOCK_HIDDEN_GRACE_MIN_MS, SESSION_LOCK_HIDDEN_GRACE_MAX_MS, push);
  if (sessionLock.exempt != null && typeof sessionLock.exempt !== 'boolean') {
    push('sessionLock.exempt must be a boolean.');
  }
}

export function validateRoleDefinitions(value) {
  const errors = [];
  const byId = {};
  if (!Array.isArray(value)) {
    return { ok: false, errors: ['roleDefinitions must be an array.'], byId };
  }
  if (value.length > MAX_ROLES) errors.push(`too many roles (max ${MAX_ROLES}).`);

  const seenKeys = new Set();
  const seenValues = new Set();

  value.forEach((entry, idx) => {
    if (entry == null || typeof entry !== 'object' || Array.isArray(entry)) {
      errors.push(`Row ${idx + 1} is not a valid object.`);
      return;
    }
    const id = isBlank(entry.key) ? `(row ${idx + 1})` : entry.key;
    const push = (msg) => {
      (byId[id] ||= []).push(msg);
      errors.push(`${entry.label || id}: ${msg}`);
    };

    if (isBlank(entry.key)) push('key is required.');
    else if (String(entry.key).length > MAX_TEXT) push(`key is too long (max ${MAX_TEXT}).`);
    if (isBlank(entry.value)) push('value (the Entra appRole name) is required.');
    else if (String(entry.value).length > MAX_TEXT) push(`value is too long (max ${MAX_TEXT}).`);
    if (isBlank(entry.label)) push('label is required.');
    else if (String(entry.label).length > MAX_TEXT) push(`label is too long (max ${MAX_TEXT}).`);
    if (entry.description != null && typeof entry.description !== 'string') push('description must be text.');
    else if (entry.description != null && entry.description.length > MAX_DESCRIPTION) push(`description is too long (max ${MAX_DESCRIPTION}).`);
    if (entry.enabled != null && typeof entry.enabled !== 'boolean') push('enabled must be a boolean.');

    const kKey = norm(entry.key);
    if (kKey) {
      if (seenKeys.has(kKey)) push(`duplicate key "${entry.key}".`);
      seenKeys.add(kKey);
    }
    const kValue = norm(entry.value);
    if (kValue) {
      if (seenValues.has(kValue)) push(`duplicate value "${entry.value}".`);
      seenValues.add(kValue);
    }

    if (isBuiltInRoleEntry(entry)) {
      if (entry.builtIn === true && !BUILT_IN_SET.has(entry.value)) {
        push(`builtIn entry "${entry.value}" is not a built-in role.`);
      }
      // SuperAdmin stays code-only; every other built-in may be governed here.
      if (entry.value === 'SuperAdmin') {
        if (entry.capabilities != null) push('SuperAdmin cannot carry capabilities (code defaults apply).');
        if (entry.scope != null) push('SuperAdmin cannot carry a scope (code defaults apply).');
        if (entry.sessionLock != null) push('SuperAdmin cannot carry sessionLock (never auto-locks).');
        return;
      }
      if (typeof entry.enabled !== 'boolean') push('built-in roles need an explicit enabled flag.');
      validatePriority(entry, push);
      validateCapabilities(entry.capabilities, push);
      validateScope(entry.scope, push);
      validateSessionLock(entry.sessionLock, push);
      return;
    }

    // Custom role — value must not collide with a built-in (case-insensitive:
    // resolution is exact-match, but a look-alike is a footgun, not a role).
    if (BUILT_IN_NORM_SET.has(kValue)) {
      push(`value "${entry.value}" collides with a built-in role name.`);
    }
    if (typeof entry.enabled !== 'boolean') {
      push('custom roles need an explicit enabled flag.');
    }
    validatePriority(entry, push);
    validateCapabilities(entry.capabilities, push);
    validateScope(entry.scope, push);
    validateSessionLock(entry.sessionLock, push);
  });

  return { ok: errors.length === 0, errors: [...new Set(errors)], byId };
}

/**
 * The key↔value pairing is IMMUTABLE across publishes: a write may add or
 * remove roles, but must not re-point an existing key at a different Entra
 * value (merge keys on `key`, resolution keys on `value` — a rebind would
 * silently retarget what a live Entra assignment grants). The UI locks the
 * field; this is the server-side backstop for non-UI publishes, enforced in
 * configWrite.js where the current value is already loaded inside the tx.
 * @returns {string[]} error messages (empty = ok)
 */
export function roleValueRebindErrors(oldValue, newValue) {
  if (!Array.isArray(oldValue) || !Array.isArray(newValue)) return [];
  const oldByKey = new Map(oldValue.filter(e => e?.key != null).map(e => [e.key, e]));
  const errors = [];
  for (const entry of newValue) {
    const prev = entry?.key != null ? oldByKey.get(entry.key) : null;
    if (prev && String(prev.value) !== String(entry.value)) {
      errors.push(`Role "${entry.key}": value cannot change ("${prev.value}" → "${entry.value}") — create a new role instead.`);
    }
  }
  return errors;
}

// ── Audit diff ────────────────────────────────────────────────────────────────

const SCALAR_FIELDS = ['label', 'value', 'description', 'enabled', 'builtIn', 'priority'];

function fieldDiffsFor(oldItem, newItem) {
  const fields = {};
  const put = (name, from, to) => {
    if (JSON.stringify(from ?? null) !== JSON.stringify(to ?? null)) {
      fields[name] = { from: from ?? null, to: to ?? null };
    }
  };
  for (const f of SCALAR_FIELDS) put(f, oldItem[f], newItem[f]);

  // Per-capability diff so the audit reads "costEdit false→true", not a blob.
  const capKeys = new Set([
    ...Object.keys(oldItem.capabilities || {}),
    ...Object.keys(newItem.capabilities || {}),
  ]);
  for (const k of capKeys) put(`capabilities.${k}`, oldItem.capabilities?.[k], newItem.capabilities?.[k]);

  put('scope.type', oldItem.scope?.type, newItem.scope?.type);
  put('scope.value', oldItem.scope?.value, newItem.scope?.value);

  put('sessionLock.inactivityMs', oldItem.sessionLock?.inactivityMs, newItem.sessionLock?.inactivityMs);
  put('sessionLock.hiddenGraceMs', oldItem.sessionLock?.hiddenGraceMs, newItem.sessionLock?.hiddenGraceMs);
  put('sessionLock.exempt', oldItem.sessionLock?.exempt, newItem.sessionLock?.exempt);
  return fields;
}

/**
 * Compact old→new diff for the audit trail and publish preview, keyed by the
 * immutable `key`. Same { added, removed, changed } shape as the schema-driven
 * keys so AuditPanel renders it unchanged.
 */
export function diffRoleDefinitions(oldValue, newValue) {
  const olds = Array.isArray(oldValue) ? oldValue : [];
  const news = Array.isArray(newValue) ? newValue : [];
  const oldById = new Map(olds.filter(i => i?.key != null).map(i => [i.key, i]));
  const newById = new Map(news.filter(i => i?.key != null).map(i => [i.key, i]));

  const added = [], removed = [], changed = [];
  for (const [k, v] of newById) {
    if (!oldById.has(k)) added.push({ id: k, label: v.label ?? v.value ?? k });
  }
  for (const [k, v] of oldById) {
    if (!newById.has(k)) removed.push({ id: k, label: v.label ?? v.value ?? k });
  }
  for (const [k, oldItem] of oldById) {
    const newItem = newById.get(k);
    if (!newItem) continue;
    const fields = fieldDiffsFor(oldItem, newItem);
    if (Object.keys(fields).length) {
      changed.push({ id: k, label: newItem.label ?? newItem.value ?? k, fields });
    }
  }
  return { added, removed, changed };
}
